import React, { useState, useEffect } from 'react';
import { Card, Progress } from 'flowbite-react';
import * as wasm from 'sched';
import { Question, Answer, AnswerOption } from 'sched';
import PairQuestion from '../../components/consolidation/PairQuestion';
import OrphanQuestion from '../../components/consolidation/OrphanQuestion';
import { useWorkspace } from '@/context/WorkspaceContext';

const ConsolidationPage: React.FC = () => {
  const { safeData } = useWorkspace();
  const [consolidationAnalysis, setConsolidationAnalysis] = useState<wasm.WrappedConsolidationAnalysis | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  // Initialize consolidation analysis on page load
  useEffect(() => {
    const initConsolidation = async () => {
      try {
        setLoading(true);
        const analysis = await wasm.DataManager.build_consolidation_analysis(safeData);
        setConsolidationAnalysis(analysis);

        // Get the first question
        const question = await analysis.get_next_question(safeData);
        if (question) {
          setCurrentQuestion(question);
        } else {
          setIsComplete(true);
        }
      } catch (err) {
        console.error('Failed to initialize consolidation analysis:', err);
        setError(`Failed to initialize: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    };

    initConsolidation();

    // Cleanup function
    return () => {
      if (consolidationAnalysis) {
        consolidationAnalysis.free();
      }
    };
  }, [safeData]);

  // Handle user's answer selection
  const handleAnswerSelected = async (option: AnswerOption) => {
    if (!consolidationAnalysis || !currentQuestion) return;

    try {
      setLoading(true);

      // Create answer object
      const answer: Answer = {
        question: currentQuestion,
        option: option
      };

      // Apply the answer
      const command = await consolidationAnalysis.apply_answer(answer);

      // If there's a command, apply it to the database
      if (command) {
        await wasm.DataManager.apply_command(safeData, command);
      }

      // Get the next question
      const nextQuestion = await consolidationAnalysis.get_next_question(safeData);
      if (nextQuestion) {
        setCurrentQuestion(nextQuestion);
      } else {
        setIsComplete(true);
      }
    } catch (err) {
      console.error('Failed to process answer:', err);
      setError(`Failed to process answer: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="p-4 flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4">
        <Card>
          <h2 className="text-xl font-bold text-red-600">Error</h2>
          <p>{error}</p>
        </Card>
      </div>
    );
  }

  // Render completion state
  if (isComplete) {
    return (
      <div className="p-4">
        <Card>
          <div className="text-center py-8">
            <h2 className="text-2xl font-bold mb-4">All done 🎉</h2>
            <p className="text-cm-text-secondary">
              You've completed all consolidation questions.
            </p>
            <button
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => {
                // initConsolidation();
              }}
            >
              Reset
            </button>
          </div>
        </Card>
      </div>
    );
  }

  const currentProgress = currentQuestion?.current_progress;
  const progressPercentage = currentProgress?.total_nodes !== undefined && currentProgress.total_nodes > 0 ? 100 * (currentProgress.nodes_prioritized ?? 0) / currentProgress.total_nodes : 0;

  // // Render the question based on its type
  return (
    <div className="p-4">
      <Card>
        <div className="flex justify-between items-center w-full gap-2">
          {currentProgress?.total_nodes !== undefined && currentProgress.total_nodes > 0 && <Progress className="flex-1" progress={progressPercentage} />} {currentProgress?.nodes_prioritized}/{currentProgress?.total_nodes} {progressPercentage.toFixed(0)}%
        </div>
        {currentQuestion && (
          <>
            {currentQuestion.decision.type === "Pair" && (
              <PairQuestion
                question={currentQuestion}
                onAnswerSelected={handleAnswerSelected}
              />
            )}
            {currentQuestion.decision.type === "ResolveOrphan" && (
              <OrphanQuestion
                question={currentQuestion}
                onAnswerSelected={handleAnswerSelected}
              />
            )}
            {currentQuestion.decision.type !== "Pair" && currentQuestion.decision.type !== "ResolveOrphan" && (
              <div className="text-red-600">
                Unknown question type: {(currentQuestion.decision as any).type}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ConsolidationPage;
